import React, { useState, useEffect } from 'react';
import { MdPalette } from "react-icons/md";
import { listPalettes } from 'store/auth';
import { useAuth } from 'components/authentication/signup-login/AuthContext';

const ColorPaletteSelection = ({ onSelectPalette }) => {
  const { isAuthenticated } = useAuth();

  // Static palettes (existing ones - always available)
  const staticPalettes = [
    {
      name: 'Blue',
      colors: [
        '#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#4292c6', // base
        '#F0E68C', 'red', '#00FFFF', '#FF00FF', '#000000' // last 5 contrast
      ]
    },
    {
      name: 'Green',
      colors: [
        '#DCECB2', '#C3DF85', '#AAD258', '#9DC54B', '#91B83E', // base
        '#000000', '#FF1493', 'orange', '#4B0082', '#8B0000' // last 5 contrast
      ]
    }
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [selectedPalette, setSelectedPalette] = useState('Blue');
  const [dynamicPalettes, setDynamicPalettes] = useState([]);
  const [allPalettes, setAllPalettes] = useState(staticPalettes);

  // Load dynamic palettes from API only if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadDynamicPalettes();
    } else {
      // If not authenticated, only use static palettes
      setAllPalettes(staticPalettes);
    }
  }, [isAuthenticated]);

  const loadDynamicPalettes = async () => {
    try {
      const response = await listPalettes();
      const palettes = response.data.palettes || [];
      
      // Transform dynamic palettes for map usage
      // Map colors = 5 base colors + last 5 contrast colors
      const transformed = palettes.map(palette => {
        const paletteData = palette.palette_data;
        const mapColors = paletteData.map_colors || [];
        const contrastColors = paletteData.contrast_colors || [];
        
        // Get last 5 contrast colors (index 5-9)
        const last5Contrast = contrastColors.slice(5, 10);
        
        // Combine: 5 base + last 5 contrast
        const colors = [...mapColors, ...last5Contrast];
        
        return {
          name: paletteData.palette_name,
          colors: colors
        };
      });
      
      setDynamicPalettes(transformed);
      // Combine static + dynamic palettes
      setAllPalettes([...staticPalettes, ...transformed]);
    } catch (error) {
      console.error('Error loading palettes:', error);
      // If error, just use static palettes
      setAllPalettes(staticPalettes);
    }
  };

  useEffect(() => {
    const defaultPalette = allPalettes.find((p) => p.name === "Blue");
    if (defaultPalette && onSelectPalette) {
      onSelectPalette(defaultPalette.colors);
    }
  }, [allPalettes, onSelectPalette]);

  const handlePaletteSelect = (palette) => {
    setSelectedPalette(palette.name);
    if (onSelectPalette) {
      onSelectPalette(palette.colors);
    }
    setIsOpen(false);
  };

  return (
    <div className='position-absolute end-0 mt-2 me-2 ' style={{ top: '45px' }}>
      {/* Toggle button */}
      <button
        className="btn btn-light border rounded-circle shadow-sm end-0 p-1"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'absolute',
          top: '10px',
          width: '50px',
          height: '50px',
        }}
      >
        <MdPalette size={30} />
      </button>

      {/* Dropdown palette */}
      {isOpen && (
        <div className=" bg-white px-1 py-3 border rounded shadow-sm end-0 " style={{ position: 'absolute', maxWidth: '200px', top: '57px', maxHeight: '400px', overflowY: 'auto' }}>
          <h6 className="fw-semibold mb-3">Colors</h6>
          <div className="d-flex flex-column">
            {allPalettes.map((palette) => (
              <div
                key={palette.name}
                className={`px-2 py-2 rounded d-flex align-items-center justify-content-start ${selectedPalette === palette.name ? 'bg-light' : ''
                  }`}
                role="button"
                onClick={() => handlePaletteSelect(palette)}
              >
                {/* Color swatches */}
                <div className="d-flex">
                  {palette.colors.map((color, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: color,
                        width: '10px',
                        height: '12px',
                      }}
                    ></div>
                  ))}
                </div>
                {/* Palette name */}
                <span className="ms-3">{palette.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPaletteSelection;